export interface Point {
  x: number
  y: number
}

export interface EdgeHitResult {
  /** 辺のインデックス（0 = point[0]とpoint[1]の間、1 = point[1]とpoint[2]の間、...） */
  edgeIndex: number
  /** マウス位置から辺への最短距離 */
  distance: number
  /** 辺上の最近点（挿入する頂点の候補位置） */
  closestPoint: Point
}

/**
 * 点から線分への最短距離と最近点を計算
 * @param point 対象点
 * @param lineStart 線分の始点
 * @param lineEnd 線分の終点
 * @returns 距離と線分上の最近点
 */
export function pointToSegmentDistance(
  point: Point,
  lineStart: Point,
  lineEnd: Point,
): { distance: number; closestPoint: Point } {
  const dx = lineEnd.x - lineStart.x
  const dy = lineEnd.y - lineStart.y
  const lengthSquared = dx * dx + dy * dy

  // 線分の長さが0の場合（始点と終点が同じ）
  if (lengthSquared === 0) {
    const distance = Math.sqrt(
      Math.pow(point.x - lineStart.x, 2) + Math.pow(point.y - lineStart.y, 2),
    )
    return { distance, closestPoint: lineStart }
  }

  // パラメータtを計算（線分上のどこに最近点があるか）
  // t = 0 → lineStart, t = 1 → lineEnd
  let t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared

  // tを[0, 1]の範囲にクランプ
  t = Math.max(0, Math.min(1, t))

  // 最近点を計算
  const closestPoint: Point = {
    x: lineStart.x + t * dx,
    y: lineStart.y + t * dy,
  }

  // 距離を計算
  const distance = Math.sqrt(
    Math.pow(point.x - closestPoint.x, 2) + Math.pow(point.y - closestPoint.y, 2),
  )

  return { distance, closestPoint }
}

/**
 * ポリゴンの辺の中でマウス位置に最も近い辺を検出
 * @param mousePos マウス位置
 * @param polygonPoints ポリゴンの頂点配列
 * @param threshold 検出閾値（この距離以内の辺のみを検出）
 * @returns 最も近い辺の情報、または閾値外の場合はnull
 */
export function findNearestEdge(
  mousePos: Point,
  polygonPoints: Point[],
  threshold: number = 20,
): EdgeHitResult | null {
  if (polygonPoints.length < 2) {
    return null
  }

  let nearestEdge: EdgeHitResult | null = null
  let minDistance = Infinity

  const numEdges = polygonPoints.length >= 3 ? polygonPoints.length : polygonPoints.length - 1

  for (let i = 0; i < numEdges; i++) {
    const start = polygonPoints[i]
    const end = polygonPoints[(i + 1) % polygonPoints.length]

    const { distance, closestPoint } = pointToSegmentDistance(mousePos, start, end)

    if (distance < minDistance && distance <= threshold) {
      minDistance = distance
      nearestEdge = {
        edgeIndex: i,
        distance,
        closestPoint,
      }
    }
  }

  return nearestEdge
}

/**
 * 表示座標をオリジナル座標に変換
 */
export function displayToOriginal(
  displayPoint: Point,
  dispW: number,
  dispH: number,
  originalWidth: number,
  originalHeight: number,
): Point {
  return {
    x: (displayPoint.x / dispW) * originalWidth,
    y: (displayPoint.y / dispH) * originalHeight,
  }
}

/**
 * オリジナル座標を表示座標に変換
 */
export function originalToDisplay(
  originalPoint: Point,
  dispW: number,
  dispH: number,
  originalWidth: number,
  originalHeight: number,
): Point {
  return {
    x: (originalPoint.x / originalWidth) * dispW,
    y: (originalPoint.y / originalHeight) * dispH,
  }
}
